import { RefObject, useState } from 'react'
import { trackInternalEvent } from '@rio/tracking'
import { useI18n } from 'builder/hooks/useI18n'
import CalendarIcon from '../assets/Calendar.svg'
import TimerIcon from '../assets/Timer.svg'
import UsersIcon from '../assets/UsersIcon'
import { TRANSLATION, truncate } from '../utils'
import { useHoverOrigin } from '../hooks/useHoverOrigin'
import {
  Card,
  Label,
  Title,
  Image,
  InfoWrapper,
  ImageWrapper,
  Description,
  Duration,
  Enrolled,
  Header,
  Meta,
  Footer,
  Badge,
  HideOnHover,
  ShowOnHover,
  Video,
} from './styles'
import { StarRating } from './StarRating'
import { SaveCourseAction } from './SaveCourseAction'

type Props = {
  courseId: string
  title: string
  description: string
  category: string
  progress?: number
  rating?: number
  reviewCount?: number
  enrolled?: string
  weeks: string
  hoursPerWeek: string
  imageSrc: string
  logoSrc?: string
  provider: string
  status: 'recomended' | 'progress'
  onClick?: () => void
  containerRef?: RefObject<HTMLElement>
  isSaved?: boolean
  videoTrailer?: string
}

export const CategoryBadge = (props: {
  status: Props['status']
  progress?: number
  children?: React.ReactNode
  position?: 'absolute' | 'relative'
}) => {
  const { position = 'absolute' } = props

  if (!props.progress) return null

  return (
    <Badge status={props.status} progress={props.progress} position={position}>
      {props.progress !== undefined ? `${props.progress}%` : props.children}
    </Badge>
  )
}

export const Media = ({ imageSrc, videoUrl }: { imageSrc: string; videoUrl?: string }) => {
  const [hovered, setHovered] = useState(false)

  return (
    <div
      style={{ width: '100%', height: '100%', position: 'relative' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {!hovered ? (
        <Image src={imageSrc} alt="Preview" />
      ) : videoUrl ? (
        <Video src={videoUrl} allow="autoplay; fullscreen" allowFullScreen />
      ) : (
        <Image src={imageSrc} alt="Preview" />
      )}
    </div>
  )
}

const flexStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
} as const

export const RecommendedCourseCard = (props: Props) => {
  const { i18n } = useI18n()
  const { ref, origin } = useHoverOrigin(props.containerRef)

  return (
    <Card ref={ref} origin={origin} onClick={props.onClick} data-testid="course-card">
      <ImageWrapper>
        <Badge status="recomended" position="absolute" data-testid="course-category">
          {props.category}
        </Badge>

        <Media imageSrc={props.imageSrc} videoUrl={props.videoTrailer || ''} />

        <HideOnHover>
          {props.logoSrc && (
            <img
              src={props.logoSrc}
              style={{ position: 'absolute', bottom: '8px', left: '8px' }}
              width="67px"
              alt={`${props.provider} logo`}
            />
          )}
        </HideOnHover>
      </ImageWrapper>

      <InfoWrapper>
        <Meta>
          <Header>
            <ShowOnHover>
              <div style={flexStyles}>
                {props.logoSrc && (
                  <img
                    src={props.logoSrc}
                    alt={`${props.provider} logo`}
                    style={{ objectFit: 'cover' }}
                    height="24px"
                  />
                )}

                <SaveCourseAction courseId={props.courseId} isAlreadySaved={props.isSaved} />
              </div>
            </ShowOnHover>
            <Label data-testid="course-provider">{props.provider}</Label>
            <Title data-testid="course-title">{truncate(props.title, 55)}</Title>
          </Header>

          {props.rating && props.reviewCount && (
            <StarRating rating={props.rating} reviewCount={props.reviewCount} />
          )}

          <Description data-testid="course-description">
            {truncate(props.description, 125)}
          </Description>
        </Meta>

        <Footer>
          <Duration data-testid="course-duration">
            <Label>
              <img src={CalendarIcon} />
              <span>{i18n.t(`${TRANSLATION}.card.weeks`, { count: props.weeks || 0 })}</span>
            </Label>
            <Label>
              <img src={TimerIcon} />
              <span>{i18n.t(`${TRANSLATION}.card.hours`, { count: props.hoursPerWeek || 0 })}</span>
            </Label>
          </Duration>

          {props.enrolled && (
            <Enrolled>
              <UsersIcon />
              <span>{i18n.t(`${TRANSLATION}.card.enrolled`, { count: props.enrolled || 0 })}</span>
            </Enrolled>
          )}
        </Footer>
      </InfoWrapper>
    </Card>
  )
}

export const ProgressCourseCard = (props: Props) => {
  const { i18n } = useI18n()
  const { ref, origin } = useHoverOrigin(props.containerRef)
  return (
    <Card ref={ref} origin={origin} onClick={props.onClick} data-testid="course-card">
      <ImageWrapper>
        {props.progress && (
          <HideOnHover>
            <Badge status="progress" progress={props.progress} position="absolute">
              {props.progress}%
            </Badge>
          </HideOnHover>
        )}

        <ShowOnHover>
          <Badge status="recomended" position="absolute">
            {props.category}
          </Badge>
        </ShowOnHover>

        <Image src={props.imageSrc} alt={props.title} />

        <HideOnHover>
          {props.logoSrc && (
            <img
              src={props.logoSrc}
              style={{ position: 'absolute', bottom: '8px', left: '8px' }}
              width="67px"
              alt={`${props.provider} logo`}
            />
          )}
        </HideOnHover>
      </ImageWrapper>

      <InfoWrapper>
        <Meta>
          <Header>
            <ShowOnHover>
              <div style={flexStyles}>
                {props.logoSrc && (
                  <img
                    src={props.logoSrc}
                    alt={`${props.provider} logo`}
                    style={{ objectFit: 'cover' }}
                    height="24px"
                  />
                )}
                {props.progress && (
                  <Badge status="progress" progress={props.progress} position="relative">
                    {props.progress}%
                  </Badge>
                )}
              </div>
            </ShowOnHover>

            <Label data-testid="course-provider">{props.provider}</Label>
            <Title data-testid="course-title">{truncate(props.title, 55)}</Title>
          </Header>

          <HideOnHover>
            <Duration>
              <Label>
                <img src={CalendarIcon} />
                <span>{i18n.t(`${TRANSLATION}.card.weeks`, { count: props.weeks || 0 })}</span>
              </Label>
              <Label>
                <img src={TimerIcon} />
                <span>
                  {i18n.t(`${TRANSLATION}.card.hours`, { count: props.hoursPerWeek || 0 })}
                </span>
              </Label>
            </Duration>
          </HideOnHover>

          <Description data-testid="course-description">
            {truncate(props.description, 125)}
          </Description>
        </Meta>

        <Footer>
          <Duration data-testid="course-duration">
            <Label>
              <img src={CalendarIcon} />
              <span>{i18n.t(`${TRANSLATION}.card.weeks`, { count: props.weeks || 0 })}</span>
            </Label>
            <Label>
              <img src={TimerIcon} />
              <span>{i18n.t(`${TRANSLATION}.card.hours`, { count: props.hoursPerWeek || 0 })}</span>
            </Label>
          </Duration>
        </Footer>
      </InfoWrapper>
    </Card>
  )
}

export const CourseCard = (props: Props) => {
  const onClick = () => {
    const payload = {
      course_name: props.title,
      course_category: props.status === 'recomended' ? 'recommended_courses' : 'my_courses',
    }

    trackInternalEvent('click_course_card', payload)

    if (props.onClick) {
      props.onClick()
    }
  }

  if (props.status === 'progress') {
    return <ProgressCourseCard {...props} onClick={onClick} />
  }
  return <RecommendedCourseCard {...props} onClick={onClick} />
}
